import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import { 
  MessageSquare, Send, Bot, User, Sparkles, Target, TrendingUp, 
  Search, AlertTriangle, BarChart3, Loader2, Info, DollarSign
} from "lucide-react";

const AIChatPage = () => {
  const { sessionToken } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [usage, setUsage] = useState(null);
  const [quickStarts, setQuickStarts] = useState([]);
  const [tenantName, setTenantName] = useState("");
  const [tenantId, setTenantId] = useState("");
  const messagesEndRef = useRef(null);

  const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");

  // Check if tenant is set
  const hasTenant = !!viewingTenantId;

  useEffect(() => {
    if (hasTenant) {
      fetchQuickStarts();
      fetchUsage();
    }
  }, [viewingTenantId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchQuickStarts = async () => {
    try {
      const response = await axios.get(
        `${API}/ai-analytics/chat/quick-starts?X-Tenant-ID=${viewingTenantId}&Authorization=${sessionToken}`
      );
      setQuickStarts(response.data.quick_starts || []);
      setTenantName(response.data.tenant_name || "");
    } catch (error) {
      console.error("Error fetching quick starts:", error);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(
        `${API}/ai-analytics/chat/usage?X-Tenant-ID=${viewingTenantId}&Authorization=${sessionToken}`
      );
      setUsage(response.data);
      setTenantId(response.data.tenant_id);
    } catch (error) {
      console.error("Error fetching usage:", error);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: "user",
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai-analytics/chat?X-Tenant-ID=${viewingTenantId}&Authorization=${sessionToken}`,
        {
          message: messageText,
          conversation_id: conversationId
        }
      );

      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString(),
        intent: response.data.intent,
        dataUsed: response.data.data_used,
        validation: response.data.validation,
        usage: response.data.usage
      };

      setMessages(prev => [...prev, assistantMessage]);
      setConversationId(response.data.conversation_id);
      
      // Refresh usage after each message
      fetchUsage();

    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage = error.response?.data?.detail || "Failed to get response";
      toast.error(errorMessage);
      
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `Sorry, I encountered an error: ${errorMessage}`,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickStart = (prompt) => {
    sendMessage(prompt);
  };

  const getQuickStartIcon = (id) => {
    const icons = {
      overview: BarChart3,
      mql_status: Target,
      goal_progress: Target,
      pipeline_health: DollarSign,
      organic_search: Search,
      anomalies: AlertTriangle
    };
    return icons[id] || MessageSquare;
  };

  // Show message if no tenant is set
  if (!hasTenant) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-amber-800">
              <AlertTriangle className="w-6 h-6" />
              <div>
                <h3 className="font-semibold">No Tenant Selected</h3>
                <p className="text-sm text-amber-700 mt-1">
                  AI Chat requires a tenant context to function. Please select a tenant from the sidebar 
                  or ensure you are logged into an account with tenant access.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="ai-chat-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#00A5A8]" />
            AI Analytics Assistant
          </h1>
          <p className="text-slate-600 mt-1">
            Ask questions about {tenantName || "your"} marketing data
          </p>
        </div>
        
        {/* Usage Badge */}
        {usage && (
          <div className="text-right">
            <Badge variant="outline" className="text-xs">
              {usage.limits.remaining} / {usage.limits.monthly_conversations} chats remaining
            </Badge>
            <p className="text-xs text-slate-500 mt-1">
              ${usage.usage.estimated_cost_usd.toFixed(4)} used this month
            </p>
          </div>
        )}
      </div>

      {/* Quick Start Buttons */}
      {messages.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Start</CardTitle>
            <CardDescription>
              Click a question to get started, or type your own below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {quickStarts.map((qs) => {
                const Icon = getQuickStartIcon(qs.id);
                return (
                  <Button
                    key={qs.id}
                    variant="outline"
                    className="h-auto py-3 px-4 justify-start text-left"
                    onClick={() => handleQuickStart(qs.label)}
                    data-testid={`quick-start-${qs.id}`}
                  >
                    <Icon className="w-4 h-4 mr-2 text-[#00A5A8] shrink-0" />
                    <span className="text-sm">{qs.label}</span>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chat Messages */}
      <Card className="min-h-[400px] flex flex-col">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[500px]">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-400">
              <div className="text-center">
                <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Start a conversation by asking a question or clicking a quick start above</p>
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-[#00A5A8] flex items-center justify-center shrink-0">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                )}
                
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    msg.role === "user"
                      ? "bg-[#00A5A8] text-white"
                      : msg.isError
                      ? "bg-red-50 border border-red-200"
                      : "bg-slate-100"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  
                  {/* Show metadata for assistant messages */}
                  {msg.role === "assistant" && msg.intent && !msg.isError && (
                    <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500 space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-[10px]">
                          {msg.intent}
                        </Badge>
                        {msg.validation?.verified && (
                          <Badge variant="outline" className="text-[10px] text-green-600 border-green-300">
                            ✓ Verified
                          </Badge>
                        )}
                      </div>
                      {msg.usage && (
                        <p className="text-slate-400">
                          {msg.usage.input_tokens + msg.usage.output_tokens} tokens • ${msg.usage.cost_usd.toFixed(6)}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {msg.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                    <User className="w-5 h-5 text-slate-600" />
                  </div>
                )}
              </div>
            ))
          )}
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-[#00A5A8] flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="bg-slate-100 rounded-lg p-3">
                <Loader2 className="w-5 h-5 animate-spin text-[#00A5A8]" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </CardContent>

        <Separator />

        {/* Input Area */}
        <div className="p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(inputValue);
            }}
            className="flex gap-2"
          >
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={`Ask about ${tenantName || "your"} marketing data...`}
              disabled={isLoading}
              className="flex-1"
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="bg-[#00A5A8] hover:bg-[#008B8E]"
              data-testid="chat-send-button"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
          
          {/* Info Footer */}
          <div className="mt-3 flex items-start gap-2 text-xs text-slate-500">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              AI responses are based on your {tenantName || "tenant"} data only. 
              Data is validated before being shown. Conversations persist for one week.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AIChatPage;
